/**
 * Smart-contract-account detection for the EVM CCTP bridge path (#137 Phase 1).
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────────────────
 * A contract account (Base Account, Safe, any ERC-4337 wallet) cannot produce an ECDSA signature that
 * recovers to its own address, so it can never use the gasless EIP-3009 `bridgeWithAuth()` path.
 * Circle's `SignatureChecker` has no ERC-6492 branch: for an account not yet deployed on the SOURCE
 * chain (where `receiveWithAuthorization` pulls the funds) `isContract(signer)` is false, so it falls
 * through to `ECRecover.recover`, which requires exactly 65 bytes. A Base Account returns a longer,
 * ERC-6492-wrapped signature. Hard revert, and not fixable on our side — circlefin/stablecoin-evm#470
 * has been open and unacknowledged since 2024-08-15. Coinbase's own x402 spec likewise routes smart
 * wallets away from EIP-3009. A counterfactual (never-deployed) address holding USDC on the source
 * chain is the COMMON case for a new passkey user, not an edge case.
 *
 * `ParseCctpBridge.bridge()` authenticates on `quote.userAddress == msg.sender` and needs no user
 * signature at all, so it works for contract accounts — and for EOAs, at the cost of one extra
 * approve transaction.
 *
 * ── THE BIAS, AND ITS LIMIT ──────────────────────────────────────────────────────────────────────
 * Because `bridge()` works for everyone, ambiguity should resolve toward `bridge()`. But that is not
 * a licence to be sloppy: sending an EOA there needlessly costs it a second transaction plus an
 * approval, and one specific mistake would hit a whole cohort that works today. Two such traps are
 * defused here explicitly — see TRAP 1 and TRAP 2 below. Both are the kind of bug that looks correct
 * in review and downgrades every user in production.
 */
import { getAccount, getBytecode } from '@wagmi/core'
import { wagmiConfig } from '@/config/wagmi'

/**
 * The Coinbase Wallet SDK's persisted signer type.
 *
 * `ScopedLocalStorage('CBWSDK', 'SignerConfigurator')` writes key `SignerType`
 * (`@coinbase/wallet-sdk/dist/sign/util.js:4-5`), and `scopedKey()` renders it as
 * `-${scope}:${module}:${key}` (`dist/core/storage/ScopedLocalStorage.js:35`). Installed SDK is 4.3.6.
 *
 * This is the authoritative discriminator because the provider RESTORES it in its constructor
 * (`dist/CoinbaseWalletProvider.js:38-42`) and only consults our configured `preference` when no
 * signer was restored. A returning user's stored signer type therefore beats `preference: 'eoaOnly'`.
 */
export const COINBASE_SIGNER_TYPE_KEY = '-CBWSDK:SignerConfigurator:SignerType'

/** Every key the SDK writes is scoped under this prefix, so its presence proves a CBWSDK connection. */
export const COINBASE_SDK_KEY_PREFIX = '-CBWSDK'

/** Values the SDK itself can store — `signerToSignerType()` returns exactly these (`sign/util.js:12-17`). */
const SIGNER_TYPE_SCW = 'scw'
const SIGNER_TYPE_WALLETLINK = 'walletlink'

/**
 * Connector ids that mean "the Coinbase Wallet SDK is the wallet currently connected", and therefore
 * that the `-CBWSDK*` localStorage keys on this origin describe THIS session rather than an old one.
 *
 * ── READ THIS BEFORE "SIMPLIFYING" THE GATE BELOW (#137 Phase 1c, FIX 1) ─────────────────────────
 * This is NOT a reversal of TRAP 1 (see `resolveEvmBridgeRoute`), and the difference is the whole
 * point:
 *
 *   TRAP 1 forbids `connector.id` as a SUFFICIENT condition — "id is coinbaseWalletSDK, therefore
 *   route to bridge()". That would downgrade every Coinbase Wallet app EOA, because the id is
 *   hardcoded for every Coinbase connection regardless of the signer actually in use.
 *
 *   This constant is `connector.id` as a NECESSARY condition — "id is NOT a Coinbase connector,
 *   therefore Coinbase's own storage key cannot be describing the wallet that is connected right
 *   now, so do not read it". It never routes anyone TO bridge(); it only decides whether a
 *   Coinbase-owned storage key is MEANINGFUL. Bytecode and the stored signer type still make the
 *   routing decision, exactly as before.
 *
 * Both statements are true simultaneously. If you are here to delete this because "we don't route on
 * connector.id", re-read the two paragraphs above — removing it restores the bug in FIX 1: the SDK
 * persists `SignerType` on connect but only clears it in `CoinbaseWalletProvider.disconnect()`, which
 * `@wagmi/core`'s `connect()` never calls. So the key outlives the Coinbase session and would be
 * applied to whatever address connects next — routing a plain MetaMask EOA to bridge() forever, at
 * one extra approve transaction per bridge.
 *
 * `baseAccount` is listed alongside `coinbaseWalletSDK` deliberately and must stay: wagmi ships a
 * SEPARATE `baseAccount()` connector, and once it is adopted a `coinbaseWalletSDK`-only gate would
 * silently stop detecting FRESH Base Account connections — a new regression created by the fix for
 * the old one. Any future Coinbase-family connector id belongs in this list too.
 */
export const COINBASE_CONNECTOR_IDS: readonly string[] = ['coinbaseWalletSDK', 'baseAccount']

/** True when `id` identifies a Coinbase-family connector — i.e. when `-CBWSDK*` keys are this session's. */
export function isCoinbaseConnectorId(id: string | undefined | null): boolean {
  return typeof id === 'string' && COINBASE_CONNECTOR_IDS.includes(id)
}

/**
 * The active connector's id, read from the SAME `wagmiConfig` that produced the signer.
 *
 * `getEthersProviderSigner()` builds the signer via `getConnectorClient(wagmiConfig, …)`
 * (`config/ethers-adapter.ts:37-38`), so the connector reported here is by construction the wallet
 * that will sign this bridge — the two cannot drift. That is why this reads the config directly
 * rather than threading a connector parameter through `useBurn.ts`: a parameter would have to be
 * sourced from this same call one level up, adding a chance to pass a stale value and buying nothing.
 * The exported signature is therefore unchanged and no call site needs updating.
 *
 * Returns `undefined` if wagmi has no active connector or the read throws. That is treated as
 * "not Coinbase" — deliberately. It is unreachable in the real flow (no connector ⇒
 * `getConnectorClient` would have thrown long before this point), and the fail direction is the safe
 * one: a smart wallet that wrongly reaches `bridgeWithAuth()` hits the pre-existing signature-length
 * guard in `useBurn.ts` and is told what to do, whereas an EOA that wrongly reaches `bridge()` just
 * silently pays an extra approve on every bridge, forever, with nothing to surface it.
 */
function readActiveConnectorId(): string | undefined {
  try {
    return getAccount(wagmiConfig).connector?.id
  } catch {
    return undefined
  }
}

export type BridgeRoute = 'bridge' | 'bridgeWithAuth'

export interface BridgeRouteDecision {
  route: BridgeRoute
  /** Stable machine-readable tag, for logs and tests. Never shown to a user. */
  reason:
    | 'flag-disabled'
    | 'bytecode-present'
    | 'stored-signer-scw'
    | 'stored-signer-unrecognized'
    | 'coinbase-sdk-present-signer-unknown'
    | 'storage-unreadable'
    | 'stored-signer-walletlink'
    | 'no-coinbase-sdk-state'
    | 'non-coinbase-connector'
    | 'bytecode-read-failed'
}

/**
 * Beta gate (#137 P1-4). OFF unless the build inlined the literal string `'true'`.
 *
 * The production gate is OMISSION, not a false value: `scripts/deploy-prod-fe.sh` builds in a clean
 * git worktree (no `.env*` files reach Vite's `loadEnv`) and then unsets every `VITE_*` that is not in
 * its ALLOWED list — a list this flag is deliberately absent from. With nothing to inline, Vite emits
 * `undefined` for this expression and the comparison is false. That is why the gate cannot fail open
 * even if an operator has the variable exported in their shell.
 */
export function isSmartWalletRoutingEnabled(): boolean {
  return import.meta.env.VITE_SMART_WALLET_ENABLED === 'true'
}

/**
 * Read the SDK's stored signer type, defensively.
 *
 * Returns the raw string when readable (the SDK stores it unencoded — `storeSignerType` calls
 * `setItem` with the bare value), `null` when the key is absent, and throws nothing: a Safari
 * private-mode / blocked-storage `localStorage` access raises, and the caller treats that as
 * "cannot rule out a smart wallet".
 */
export function readStoredCoinbaseSignerType(): { ok: true; value: string | null } | { ok: false } {
  try {
    return { ok: true, value: globalThis.localStorage?.getItem(COINBASE_SIGNER_TYPE_KEY) ?? null }
  } catch {
    return { ok: false }
  }
}

/** True when ANY `-CBWSDK*` key exists — i.e. the Coinbase SDK has state on this origin. */
export function hasCoinbaseSdkState(): { ok: true; value: boolean } | { ok: false } {
  try {
    const ls = globalThis.localStorage
    if (!ls) return { ok: true, value: false }
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i)
      if (typeof key === 'string' && key.startsWith(COINBASE_SDK_KEY_PREFIX)) return { ok: true, value: true }
    }
    return { ok: true, value: false }
  } catch {
    return { ok: false }
  }
}

/**
 * Decide which contract entrypoint this account must use on `chainId`.
 *
 * ── TRAP 1: never discriminate on `connector.id` ─────────────────────────────────────────────────
 * `@wagmi/connectors` hardcodes `id: 'coinbaseWalletSDK'` (`dist/esm/coinbaseWallet.js:15` and `:210`)
 * for EVERY Coinbase connection — identical regardless of `preference` or the signer actually in use.
 * Routing on it would sweep in every Coinbase Wallet APP EOA user and downgrade them from one gasless
 * transaction to two plus an approval. That is a systematic regression of a working cohort, not a
 * benign false positive. `useBurn.route.test.ts` pins that case.
 *
 * ── TRAP 2: viem returns `undefined`, not `'0x'`, for an account with no code ─────────────────────
 * `getCode` explicitly maps `'0x'` → `undefined` (`viem/_esm/actions/public/getCode.js`, the
 * `if (hex === '0x') return undefined` line), and `@wagmi/core`'s `getBytecode` is a thin passthrough
 * to it. So the natural-looking predicate `bytecode !== '0x'` is TRUE for every plain EOA and would
 * route the ENTIRE user base to `bridge()`. Both empty shapes must be treated as "no code".
 *
 * ── EIP-7702 ─────────────────────────────────────────────────────────────────────────────────────
 * A 7702-delegated EOA reports non-empty bytecode and therefore routes to `bridge()`. Under the
 * fail-safe bias that is harmless — it costs one approve — so there is deliberately no carve-out.
 *
 * Reads code through the app's OWN wagmi transport, never the wallet's provider: asking a wallet
 * whether it is a contract is asking it a question about itself.
 */
export async function resolveEvmBridgeRoute(params: {
  address: `0x${string}`
  chainId: number
}): Promise<BridgeRouteDecision> {
  if (!isSmartWalletRoutingEnabled()) {
    // Gate closed ⇒ every account routes exactly as it did before Phase 1, contract accounts included.
    return { route: 'bridgeWithAuth', reason: 'flag-disabled' }
  }

  let bytecode: string | undefined | null
  try {
    // The cast is only to satisfy wagmi's literal chain-id union. `chainId` comes from
    // CCTP_CONFIG[evmChain].chainId, which is by construction one of the configured chains; an
    // unconfigured id would make getBytecode throw, and the catch below routes that to bridgeWithAuth.
    bytecode = await getBytecode(wagmiConfig, {
      address: params.address,
      chainId: params.chainId as (typeof wagmiConfig)['chains'][number]['id'],
    })
  } catch {
    // An RPC failure is NOT evidence of a smart wallet. Falling back to `bridgeWithAuth` keeps the
    // working EOA cohort working during an RPC blip; a smart wallet that lands there still cannot
    // lose funds — it hits the pre-existing length guard in useBurn.ts and is told what to do.
    return { route: 'bridgeWithAuth', reason: 'bytecode-read-failed' }
  }

  // TRAP 2: '' / '0x' / undefined / null all mean "no code deployed at this address".
  const hasCode = typeof bytecode === 'string' && bytecode !== '0x' && bytecode !== ''
  if (hasCode) return { route: 'bridge', reason: 'bytecode-present' }

  // No code. The account may still be a COUNTERFACTUAL smart wallet — deployed lazily on first use,
  // which is the default state of a fresh passkey Base Account. The SDK's stored signer type is what
  // distinguishes that from a plain EOA.

  // ── FIX 1 (#137 Phase 1c): the stored key is only evidence if COINBASE IS CONNECTED RIGHT NOW ────
  // Everything below this line reads `-CBWSDK*` localStorage, which the Coinbase SDK writes on
  // connect and clears only in its own `disconnect()` — a method wagmi's `connect()` never calls. So
  // those keys routinely outlive the Coinbase session (connect a Base Account, come back tomorrow
  // with MetaMask) and, ungated, get applied to whatever address happens to be connected.
  //
  // This gate is `connector.id` as a NECESSARY condition, which is NOT the TRAP 1 prohibition in this
  // function's header above on `connector.id` as a SUFFICIENT condition — see COINBASE_CONNECTOR_IDS
  // for the full
  // distinction before changing this. It routes nobody TO bridge(); it only refuses to believe
  // another wallet's leftover storage. Bytecode and stored type still decide the route.
  //
  // Placement is load-bearing: this sits AFTER the bytecode branch above, never before it. A
  // deployed contract account arriving over injected or WalletConnect must still route to bridge()
  // on its own bytecode evidence, which owes nothing to Coinbase storage. The gate covers the
  // stored-key branch ONLY.
  //
  // It must also cover `hasCoinbaseSdkState()` further down, not just the SignerType read: the stale
  // `-CBWSDK:SignerConfigurator:SignerType` key is itself a `-CBWSDK*` key, so gating only the
  // signer-type read would leave the very same stale entry satisfying the SDK-state branch and
  // returning bridge() anyway. Hence one early return covering the whole storage-derived block.
  if (!isCoinbaseConnectorId(readActiveConnectorId())) {
    return { route: 'bridgeWithAuth', reason: 'non-coinbase-connector' }
  }

  const stored = readStoredCoinbaseSignerType()
  if (!stored.ok) {
    // localStorage threw. We cannot rule out a stored 'scw', so take the path that works either way.
    return { route: 'bridge', reason: 'storage-unreadable' }
  }

  if (stored.value === SIGNER_TYPE_SCW) {
    return { route: 'bridge', reason: 'stored-signer-scw' }
  }

  if (stored.value === SIGNER_TYPE_WALLETLINK) {
    // The Coinbase Wallet APP / extension EOA cohort. This is the branch TRAP 1 would have destroyed:
    // proven EOA, keeps the one-transaction gasless path, byte-for-byte as before.
    return { route: 'bridgeWithAuth', reason: 'stored-signer-walletlink' }
  }

  // Neither known value. Two sub-cases, and they must not be conflated:
  const sdkState = hasCoinbaseSdkState()
  if (!sdkState.ok) return { route: 'bridge', reason: 'storage-unreadable' }

  if (sdkState.value) {
    // Coinbase SDK state EXISTS on this origin but the signer type is absent, empty or a value this
    // build does not recognise (a newer SDK could add one). Not proven EOA ⇒ fail safe to bridge().
    return {
      route: 'bridge',
      reason: stored.value === null ? 'coinbase-sdk-present-signer-unknown' : 'stored-signer-unrecognized',
    }
  }

  // A Coinbase connector is active (the gate above passed) but the SDK has left no state on this
  // origin at all — e.g. storage cleared between connect and bridge. Non-Coinbase wallets no longer
  // arrive here; they return `non-coinbase-connector` at the gate above. Nothing supports the
  // counterfactual-smart-wallet hypothesis, and routing this group to bridge() would downgrade
  // working users for no evidence. Proven-enough EOA.
  return { route: 'bridgeWithAuth', reason: 'no-coinbase-sdk-state' }
}
